/**
 * Per-entity-type drill-in page — what the user lands on after clicking
 * "Show all N" on an entity-type card in the Research browse grid.
 *
 * Visual reference: storybook `Prototypes/ModernDesign/Pages/Research Entity List`.
 *
 * Responsibilities:
 *  - URL: `/cases/:caseId/search/entities/:typeLabel`
 *  - Custom typed hero (entity color identity carries from the browse card)
 *    + back arrow to the Research index
 *  - Filter input (debounced) → narrows the list
 *  - Sort control (chip-style toggles, persists in URL query string)
 *  - Row click → opens the entity detail sheet (shared with Investigation
 *    → Evidence). Multi-select is checkbox-only; the body of the row is
 *    reserved for the detail action.
 *  - Sticky bulk-action toolbar with Add to graph + destructive Delete
 *    placeholder. Export and Add-to-investigation removed per product call.
 *  - Infinite scroll via React Query useInfiniteQuery
 */
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownUp,
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  type LucideIcon,
  Minus,
  Network,
  Search as SearchIcon,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { addContributionToActive } from '@/components/agent/tools/_shared/applyContribution'
import { PageShell } from '@/components/layout/PageShell'
import { ENTITY_ICON_COMPONENTS, LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card } from '@/components/ui/card'
import { toast } from '@/components/ui/sonner'
import { DocumentFilter } from '@/features/search/DocumentFilter'
import { useDocumentFilter } from '@/features/search/hooks/use-document-filter'
import { useEntityTypeList } from '@/features/search/hooks/use-entity-type-list'
import { useDebounced } from '@/hooks/use-debounced'
import { useIsMobile } from '@/hooks/use-mobile'
import { useCaseContext } from '@/pages/cases/CaseContext'
import { api } from '@/services/api'
import {
  buildGraphViewFromEntityHits,
  type EntityHit,
  type EntityListOrder,
  type EntityListSort,
  getEntityHitDisplayName,
} from '@/services/api/case-search'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import { cn } from '@/utils/cn'
import { EntityDetailSheet } from './EntityDetailSheet'

// Sort options exposed in the UI. `score` is intentionally omitted — it's
// only meaningful when a query is scoring entities by fuzzy-match relevance,
// and this page exposes only a substring filter (no scoring). Keeping it in
// the UI would invite a confusing "sort by score with no query".
const ALLOWED_SORTS: EntityListSort[] = ['mentions', 'name']
const FILTER_DEBOUNCE_MS = 200

export default function EntityTypeListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { caseId } = useCaseContext()
  const { typeLabel: rawTypeLabel } = useParams<{ typeLabel: string }>()
  const typeLabel = rawTypeLabel ? decodeURIComponent(rawTypeLabel) : ''
  const [searchParams, setSearchParams] = useSearchParams()
  const sort = readSort(searchParams.get('sort'))
  const order = readOrder(searchParams.get('order'))
  const [filterInput, setFilterInput] = useState('')
  const debouncedFilter = useDebounced(filterInput, FILTER_DEBOUNCE_MS)
  const [document] = useDocumentFilter(caseId)
  const list = useEntityTypeList({
    caseId,
    typeLabel,
    query: debouncedFilter,
    sort,
    order,
    documentId: document?.id,
  })
  const items = useMemo<EntityHit[]>(
    () => list.data?.pages.flatMap(p => p.items) ?? [],
    [list.data]
  )
  const total = list.data?.pages[0]?.total ?? 0
  const { selected, toggle, setMany, clear } = useSelection()
  const [detailHit, setDetailHit] = useState<EntityHit | null>(null)
  const color = LABEL_COLORS[typeLabel] ?? LABEL_COLORS.Default
  const Icon = ENTITY_ICON_COMPONENTS[typeLabel] ?? ENTITY_ICON_COMPONENTS.Default
  const displayLabel = t(`entityType.${typeLabel}`, { defaultValue: typeLabel })
  const storeApi = useInvestigationsStoreApi()
  const queryClient = useQueryClient()
  // On the phone the hero scrolls away with the list instead of staying pinned
  // above the scroll area (PageShell's "full" layout pins it).
  const isMobile = useIsMobile()

  const selectedHits = useMemo(
    () => items.filter(item => selected.has(item.entity_id)),
    [items, selected]
  )

  // "Select all" operates only on the rows fetched so far (infinite scroll) — it
  // never reaches into pages the user hasn't loaded. allSelected drives both the
  // master checkbox state and the toggle direction.
  const allSelected = items.length > 0 && items.every(item => selected.has(item.entity_id))
  const onToggleSelectAll = () => {
    const ids = items.map(item => item.entity_id)
    setMany(ids, !allSelected)
  }

  const updateSort = (s: EntityListSort, o: EntityListOrder) => {
    const next = new URLSearchParams(searchParams)
    next.set('sort', s)
    next.set('order', o)
    setSearchParams(next, { replace: true })
  }

  const onAddToGraph = () => {
    if (selectedHits.length === 0) return
    const label = t('research.list.bulk.addedToGraphLabel', {
      count: selectedHits.length,
      type: displayLabel,
    })
    const view = buildGraphViewFromEntityHits(selectedHits, caseId, label)
    const result = addContributionToActive({ store: storeApi, view, label })
    if (result.contributionId) {
      toast.success(t('research.list.bulk.addedToGraphToast', { count: selectedHits.length }))
      clear()
      navigate(`/cases/${caseId}/graph`)
    } else {
      toast.message(t('research.list.bulk.alreadyInGraph'))
    }
  }

  const onConfirmDelete = async () => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    try {
      await api.graph.bulkDeleteNodes(caseId, ids)
      toast.success(t('research.list.bulk.deletedToast', { count: ids.length }))
      // The list, browse grid, and graph all read from Neo4j — wipe their
      // caches so the deleted nodes don't linger in any view.
      queryClient.invalidateQueries({ queryKey: ['case-entities-by-type'] })
      queryClient.invalidateQueries({ queryKey: ['graph'] })
      clear()
      list.refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('research.list.bulk.deleteFailed'))
    }
  }

  const hero = (
    <TypeHero
      label={displayLabel}
      total={total}
      color={color.fill}
      Icon={Icon}
      onBack={() => navigate(`/cases/${caseId}/search`)}
    />
  )

  return (
    <PageShell layout="full" hero={isMobile ? undefined : hero}>
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-8 md:px-6">
        {isMobile && <div className="py-4">{hero}</div>}
        <div className="space-y-4">
          <ToolBar
            filterInput={filterInput}
            onFilterChange={setFilterInput}
            displayLabel={displayLabel}
            sort={sort}
            order={order}
            onSortChange={updateSort}
          >
            {/* Rows of the previous document are no longer on the list to act on. */}
            <DocumentFilter
              caseId={caseId}
              onChange={clear}
              className="h-10 w-full rounded-xl md:w-64"
            />
          </ToolBar>
          <ListBody
            list={list}
            items={items}
            color={color.fill}
            displayLabel={displayLabel}
            debouncedFilter={debouncedFilter}
            documentFiltered={Boolean(document)}
            selected={selected}
            allSelected={allSelected}
            onToggleSelectAll={onToggleSelectAll}
            onToggleSelect={toggle}
            onOpenDetail={setDetailHit}
          />
          {items.length > 0 && (
            <div className="px-0.5 text-2xs text-muted-foreground/80">
              {t('research.list.showing', { shown: items.length, total })}
            </div>
          )}
        </div>
        {selected.size > 0 && (
          <BulkBar
            count={selected.size}
            displayLabel={displayLabel}
            onClear={clear}
            onAddToGraph={onAddToGraph}
            onConfirmDelete={onConfirmDelete}
          />
        )}
      </div>
      <EntityDetailSheet caseId={caseId} hit={detailHit} onClose={() => setDetailHit(null)} />
    </PageShell>
  )
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function TypeHero({
  label,
  total,
  color,
  Icon,
  onBack,
}: {
  label: string
  total: number
  color: string
  Icon: LucideIcon
  onBack: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-1.5 text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground/80 transition-colors hover:text-foreground max-md:-my-1.5 max-md:py-1.5"
      >
        <ArrowLeft className="size-3" />
        {t('research.list.backToResearch')}
      </button>
      <div className="flex items-center gap-3 md:gap-4">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${color}1a`, color }}
        >
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3">
            <h1 className="truncate text-xl font-semibold leading-tight text-foreground md:text-2xl">
              {label}
            </h1>
            <span className="font-mono text-sm tabular-nums text-muted-foreground">
              {total.toLocaleString()}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground/80">{t('research.list.heroHint')}</p>
        </div>
      </div>
    </div>
  )
}

// ─── Toolbar (filter + sort) ─────────────────────────────────────────────────

function ToolBar({
  filterInput,
  onFilterChange,
  displayLabel,
  sort,
  order,
  onSortChange,
  children,
}: {
  filterInput: string
  onFilterChange: (v: string) => void
  displayLabel: string
  sort: EntityListSort
  order: EntityListOrder
  onSortChange: (s: EntityListSort, o: EntityListOrder) => void
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Card className="flex h-10 min-w-[260px] flex-1 flex-row items-center gap-2 rounded-xl p-0 pl-3 pr-1.5 focus-within:border-ring">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={filterInput}
          onChange={e => onFilterChange(e.target.value)}
          placeholder={t('research.list.filterPlaceholder', { type: displayLabel })}
          className="h-full min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground/50 md:text-sm"
        />
        {filterInput && (
          <button
            type="button"
            onClick={() => onFilterChange('')}
            aria-label={t('research.list.clearFilter')}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:p-1"
          >
            <X className="size-3.5" />
          </button>
        )}
      </Card>
      {children}
      <SortChips sort={sort} order={order} onChange={onSortChange} />
    </div>
  )
}

function SortChips({
  sort,
  order,
  onChange,
}: {
  sort: EntityListSort
  order: EntityListOrder
  onChange: (s: EntityListSort, o: EntityListOrder) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-10 items-center gap-1 rounded-xl border border-border bg-card px-1">
      <ArrowDownUp className="ml-1.5 size-3 text-muted-foreground/60" />
      {ALLOWED_SORTS.map(s => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s, order)}
          className={cn(
            'h-8 rounded-lg px-3 text-xs font-medium transition-colors md:h-7 md:px-2.5',
            sort === s ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {t(`research.list.sort.${s}`)}
        </button>
      ))}
      <span className="mx-0.5 h-5 w-px bg-border" />
      <button
        type="button"
        onClick={() => onChange(sort, order === 'desc' ? 'asc' : 'desc')}
        title={t(`research.list.order.${order}`)}
        className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:size-7"
      >
        <span className="text-sm leading-none">{order === 'desc' ? '↓' : '↑'}</span>
      </button>
    </div>
  )
}

// ─── List body ───────────────────────────────────────────────────────────────

function ListBody({
  list,
  items,
  color,
  displayLabel,
  debouncedFilter,
  documentFiltered,
  selected,
  allSelected,
  onToggleSelectAll,
  onToggleSelect,
  onOpenDetail,
}: {
  list: ReturnType<typeof useEntityTypeList>
  items: EntityHit[]
  color: string
  displayLabel: string
  debouncedFilter: string
  documentFiltered: boolean
  selected: Set<string>
  allSelected: boolean
  onToggleSelectAll: () => void
  onToggleSelect: (id: string) => void
  onOpenDetail: (hit: EntityHit) => void
}) {
  const { t } = useTranslation()
  if (list.isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-16 text-sm text-muted-foreground">
        <Loader2 className="mr-2 size-3.5 animate-spin" />
        {t('research.loading')}
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <Card
        variant="dashed"
        className="wrap-anywhere px-6 py-12 text-center text-sm text-muted-foreground"
      >
        {debouncedFilter
          ? t('research.list.empty.filtered', { query: debouncedFilter })
          : t(documentFiltered ? 'research.list.empty.inDocument' : 'research.list.empty.none', {
              type: displayLabel,
            })}
      </Card>
    )
  }
  const someSelected = !allSelected && items.some(item => selected.has(item.entity_id))
  return (
    <Card className="overflow-hidden p-0">
      <SelectAllHeader
        checked={allSelected}
        indeterminate={someSelected}
        onToggle={onToggleSelectAll}
      />
      <ul className="divide-y divide-border/40">
        {items.map(hit => (
          <EntityListRow
            key={hit.entity_id}
            hit={hit}
            color={color}
            hideSource={documentFiltered}
            selected={selected.has(hit.entity_id)}
            onToggleSelect={() => onToggleSelect(hit.entity_id)}
            onOpen={() => onOpenDetail(hit)}
          />
        ))}
      </ul>
      {list.hasNextPage && (
        <button
          type="button"
          onClick={() => list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
          className="flex w-full items-center justify-center gap-1.5 border-t border-border/40 bg-muted/10 px-3 py-2.5 text-2xs font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground disabled:opacity-50"
        >
          {list.isFetchingNextPage ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <ChevronRight className="size-3" />
          )}
          {t('research.list.loadMore')}
        </button>
      )}
    </Card>
  )
}

// Master toggle pinned to the top of the list. A plain button (not a
// label-wrapped input) keeps the click semantics unambiguous; the box mirrors
// the row checkbox visual so they line up, and `indeterminate` (some-but-not-all
// loaded rows selected) renders a dash instead of a check.
function SelectAllHeader({
  checked,
  indeterminate,
  onToggle,
}: {
  checked: boolean
  indeterminate: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const active = checked || indeterminate
  const label = checked ? t('research.list.deselectAll') : t('research.list.selectAll')
  return (
    <button
      type="button"
      onClick={onToggle}
      role="checkbox"
      aria-checked={checked ? 'true' : indeterminate ? 'mixed' : 'false'}
      className="flex w-full items-center gap-2 border-b border-border/40 bg-muted/20 py-3 pl-4 pr-4 text-left text-2xs font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground md:py-2"
    >
      <span
        className={cn(
          'flex size-4 items-center justify-center rounded border transition-colors',
          active
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-card text-transparent'
        )}
      >
        {checked ? (
          <Check className="size-3" strokeWidth={3} />
        ) : indeterminate ? (
          <Minus className="size-3" strokeWidth={3} />
        ) : null}
      </span>
      {label}
    </button>
  )
}

function EntityListRow({
  hit,
  color,
  hideSource,
  selected,
  onToggleSelect,
  onOpen,
}: {
  hit: EntityHit
  color: string
  hideSource: boolean
  selected: boolean
  onToggleSelect: () => void
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const name = getEntityHitDisplayName(hit)
  const source = hideSource ? null : hit.document_name
  const initials =
    name
      .split(/\s+/)
      .slice(0, 2)
      .map(s => s[0]?.toUpperCase() ?? '')
      .join('') || '·'
  // Checkbox click is isolated so it never bubbles up to the row button.
  const onCheckboxClick = (e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation()
    onToggleSelect()
  }
  return (
    <li
      className={cn(
        'group/row relative flex items-stretch transition-colors',
        selected ? 'bg-primary/[0.06]' : 'hover:bg-muted/30'
      )}
    >
      {/* Checkbox is its own click target — explicitly NOT inside the row button
          so toggling selection never opens the detail sheet. Always visible. */}
      <label
        className="flex shrink-0 cursor-pointer items-center pl-4 pr-2"
        onClick={onCheckboxClick}
      >
        <span
          className={cn(
            'flex size-4 items-center justify-center rounded border transition-colors',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-transparent group-hover/row:border-primary/40'
          )}
        >
          {selected && <Check className="size-3" strokeWidth={3} />}
        </span>
        <input
          type="checkbox"
          checked={selected}
          onChange={onCheckboxClick}
          aria-label={t('research.list.selectRow')}
          className="sr-only"
        />
      </label>
      {/* Body of the row opens the detail sheet. */}
      <button
        type="button"
        onClick={onOpen}
        className="grid min-w-0 flex-1 cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-0.5 py-2.5 pr-4 text-left md:flex"
      >
        <span
          className="col-start-1 row-span-2 row-start-1 flex size-8 shrink-0 items-center justify-center rounded-md font-mono text-2xs font-semibold tabular-nums"
          style={{ backgroundColor: `${color}1a`, color }}
        >
          {initials}
        </span>
        {/* Below md the row is a grid: the name takes the whole first line (and
            may wrap) instead of sharing it with the mentions count; the source
            document and the count sit on the second line. From md it is the
            one-line flex row, where the col-/row- placement classes do nothing. */}
        <div className="contents min-w-0 flex-1 md:block">
          <div className="col-span-2 col-start-2 row-start-1 text-sm font-medium text-foreground max-md:line-clamp-3 max-md:break-words md:truncate">
            {name}
          </div>
          {source && (
            <div className="col-start-2 row-start-2 truncate text-[10.5px] text-muted-foreground/80">
              {source}
            </div>
          )}
        </div>
        <div
          className={cn(
            'row-start-2 flex shrink-0 items-baseline gap-1.5 font-mono tabular-nums',
            // Next to the source document, or under the name without one.
            source ? 'col-start-3' : 'col-start-2 justify-self-start'
          )}
        >
          <span className="text-xs font-semibold text-foreground md:text-sm">
            {Math.round(hit.score).toLocaleString()}
          </span>
          <span className="text-3xs uppercase tracking-wider text-muted-foreground/70">
            {t('research.list.mentions')}
          </span>
        </div>
        <ChevronRight className="col-start-4 row-span-2 row-start-1 size-3.5 shrink-0 text-muted-foreground/30 transition-colors group-hover/row:text-muted-foreground/70" />
      </button>
    </li>
  )
}

// ─── Bulk-action command bar ─────────────────────────────────────────────────

function BulkBar({
  count,
  displayLabel,
  onClear,
  onAddToGraph,
  onConfirmDelete,
}: {
  count: number
  displayLabel: string
  onClear: () => void
  onAddToGraph: () => void
  onConfirmDelete: () => void | Promise<void>
}) {
  const { t } = useTranslation()
  return (
    <div className="sticky bottom-4 left-0 right-0 z-10 mx-auto mt-4 flex w-fit max-w-full items-center gap-1 whitespace-nowrap rounded-full border border-border bg-card/95 px-2 py-1.5 shadow-lg backdrop-blur">
      <span className="inline-flex items-center gap-2 px-2 text-sm font-medium text-foreground">
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 font-mono text-2xs font-bold tabular-nums text-primary-foreground">
          {count}
        </span>
        {/* Phone: the count badge alone, so the actions fit on one line. */}
        <span className="max-sm:sr-only">{t('research.list.selected')}</span>
      </span>
      <span className="mx-1 h-5 w-px bg-border" />
      <BulkAction
        icon={<Network className="size-3.5" />}
        label={t('research.list.bulk.addToGraph')}
        onClick={onAddToGraph}
      />
      <DeleteAction count={count} displayLabel={displayLabel} onConfirm={onConfirmDelete} />
      <span className="mx-1 h-5 w-px bg-border" />
      <button
        type="button"
        onClick={onClear}
        aria-label={t('research.list.bulk.clear')}
        className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:size-7"
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}

function BulkAction({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:py-1"
    >
      {icon}
      {label}
    </button>
  )
}

function DeleteAction({
  count,
  displayLabel,
  onConfirm,
}: {
  count: number
  displayLabel: string
  onConfirm: () => void | Promise<void>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const handleConfirm = async () => {
    setPending(true)
    try {
      await onConfirm()
      setOpen(false)
    } finally {
      setPending(false)
    }
  }
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-2 text-xs text-destructive/80 transition-colors hover:bg-destructive/10 hover:text-destructive md:py-1"
      >
        <Trash2 className="size-3.5" />
        {t('research.list.bulk.delete')}
      </button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('research.list.bulk.deleteConfirmTitle', { count, type: displayLabel })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('research.list.bulk.deleteConfirmBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            {t('research.list.bulk.deleteConfirmCancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {pending ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-1.5 size-3.5" />
            )}
            {t('research.list.bulk.deleteConfirmAction', { count })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ─── Local hooks + utilities ─────────────────────────────────────────────────

function useSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const setMany = (ids: string[], shouldSelect: boolean) =>
    setSelected(prev => {
      const next = new Set(prev)
      for (const id of ids) {
        if (shouldSelect) next.add(id)
        else next.delete(id)
      }
      return next
    })
  const clear = () => setSelected(new Set())
  return { selected, toggle, setMany, clear }
}

function readSort(raw: string | null): EntityListSort {
  if (raw && (ALLOWED_SORTS as string[]).includes(raw)) return raw as EntityListSort
  return 'mentions'
}

function readOrder(raw: string | null): EntityListOrder {
  return raw === 'asc' ? 'asc' : 'desc'
}
